// src/pages/kpis/compras/DiasColocacionOC.jsx
// KPI de Días de Colocación de OC — pestañas Resumen / Histórico / Detalle.
//
// Reglas del KPI (acordadas con Compras):
//  - Dos tiempos en días hábiles (lunes a viernes, sin festivos):
//      1) desde la emisión de la requisición hasta la OC  (meta: 2 días)
//      2) desde la aprobación final (última firma) hasta la OC
//  - OCs colocadas ANTES de la aprobación final: no entran al tiempo 2;
//    se muestran como % aparte.
//  - Pago de factura: aparte, no entra a ningún promedio.
//  - Sin requisición = sin folio + folio no encontrado; no entra a promedios.
//  - OCs anteriores a la App de Compras (29/04/2026): "antes de la app".

import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { Chart, registerables } from 'chart.js';
import * as XLSX from 'xlsx';
import { FiDownload, FiRefreshCw, FiInfo, FiChevronLeft, FiChevronRight, FiChevronDown, FiChevronUp, FiAlertTriangle } from 'react-icons/fi';
import api from '../../../services/api';
import s from '../../mantenimiento/estilos';

Chart.register(...registerables);

const BASE = '/api/kpis/dias-colocacion-oc';
const COLOR = { verde: '#3E7C5A', amarillo: '#B8862E', rojo: '#B23A48', morado: '#4A1E5C', lila: '#A98BB8', gris: '#8A7690', azul: '#4F7CA8' };
const MESES_ARRANQUE = ['2026-05', '2026-06']; // arranque de la App de Compras (se muestran con nota)

const CLASIFICACION = {
  con_requisicion: { label: 'Con requisición', tipo: 'verde' },
  pago_factura: { label: 'Pago de factura', tipo: 'azul' },
  sin_folio: { label: 'Sin folio', tipo: 'amarillo' },
  folio_no_encontrado: { label: 'Folio no encontrado', tipo: 'rojo' },
  antes_app: { label: 'Antes de la app', tipo: 'gris' },
};
const ESTADO_REQUI = {
  borrador: 'Borrador', enviada: 'Enviada', en_revision_compras: 'En revisión de Compras', rechazada_compras: 'Rechazada por Compras',
  aprobada_compras: 'Aprobada por Compras', en_aprobacion: 'En aprobación', rechazada: 'Rechazada', aprobada_final: 'Aprobada',
  cancelada: 'Cancelada', recibida: 'Recibida', pendiente_cierre: 'Pendiente de cierre',
};
const ROL = { compras: 'Compras', jefe: 'Jefe', gerente_operaciones: 'Gerente de operaciones', gerencia_adm: 'Gerencia administrativa' };

// ─── Helpers ───
const num = (v) => (v === null || v === undefined || v === '' ? null : parseFloat(v));
const fmtDias = (v) => (num(v) === null ? '—' : `${num(v).toLocaleString('es-MX', { maximumFractionDigits: 2 })}`);
const fmtPct = (v) => (num(v) === null ? '—' : `${num(v).toFixed(1)}%`);
const fmtMoneda = (v) => (num(v) === null ? '—' : num(v).toLocaleString('es-MX', { style: 'currency', currency: 'MXN', maximumFractionDigits: 0 }));
/** Acepta Date, ISO o "YYYY-MM-DD HH:MM:SS" (Safari no entiende el espacio) */
const aFecha = (v) => (v ? new Date(typeof v === 'string' && v.length === 19 && v[10] === ' ' ? v.replace(' ', 'T') : v) : null);
const fmtFecha = (v) => (v ? aFecha(v).toLocaleDateString('es-MX') : '—');
const fmtFechaHora = (v) => (v ? aFecha(v).toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'short' }) : '—');
const nombreMes = (d) => (d ? new Date(d).toLocaleDateString('es-MX', { month: 'long', year: 'numeric' }) : '—');
const nombreMesCorto = (d) => new Date(d).toLocaleDateString('es-MX', { month: 'short', year: '2-digit' });
const pad = (n) => String(n).padStart(2, '0');
const claveMes = (d) => { const x = new Date(d); return `${x.getFullYear()}-${pad(x.getMonth() + 1)}`; };

function rangoDeMes(mes) {
  const d = new Date(mes);
  const y = d.getFullYear();
  const m = d.getMonth();
  return { desde: `${y}-${pad(m + 1)}-01`, hasta: `${y}-${pad(m + 1)}-${pad(new Date(y, m + 1, 0).getDate())}` };
}

function semaforoDias(v, meta) {
  if (num(v) === null || meta === null) return 'gris';
  if (num(v) <= meta) return 'verde';
  if (num(v) <= meta + 2) return 'amarillo';
  return 'rojo';
}

/** ¿El mes ya tiene datos de la App de Compras? (los meses 100% "antes de la app" no se grafican) */
const mesConApp = (r) => Number(r.total_ordenes) > Number(r.antes_app || 0);

function Grafica({ config, alto = 280 }) {
  const ref = useRef(null);
  useEffect(() => {
    if (!ref.current) return undefined;
    const chart = new Chart(ref.current, config);
    return () => chart.destroy();
  }, [config]);
  return (
    <div style={{ position: 'relative', height: alto }}>
      <canvas ref={ref} />
    </div>
  );
}

const Tag = ({ children, tipo = 'gris', title }) => (
  <span style={{ ...x.tag, ...x[`tag_${tipo}`] }} title={title}>{children}</span>
);

export default function DiasColocacionOC() {
  const [tab, setTab] = useState('resumen');
  const [resumen, setResumen] = useState([]);
  const [meta, setMeta] = useState(2);
  const [ultimaSync, setUltimaSync] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);
  const [mensaje, setMensaje] = useState(null);
  const [sincronizando, setSincronizando] = useState(false);

  const filtrosVacios = { desde: '', hasta: '', clasificacion: '', aprobacion: '', busqueda: '' };
  const [filtros, setFiltros] = useState(filtrosVacios);
  const [pagina, setPagina] = useState(1);
  const [porPagina, setPorPagina] = useState(50);
  const [detalle, setDetalle] = useState({ data: [], total: 0, totalPaginas: 1 });
  const [cargandoDetalle, setCargandoDetalle] = useState(false);
  const [exportando, setExportando] = useState(false);
  const [expandida, setExpandida] = useState(null);

  async function cargarResumen() {
    const res = await api.get(`${BASE}/resumen`);
    setResumen(res.data.data);
    if (res.data.meta !== null && res.data.meta !== undefined) setMeta(res.data.meta);
    setUltimaSync(res.data.ultimaSync);
    return res.data.data;
  }

  async function cargarDetalle(f = filtros, p = pagina, pp = porPagina) {
    try {
      setCargandoDetalle(true);
      const params = { pagina: p, porPagina: pp };
      Object.entries(f).forEach(([k, v]) => { if (v) params[k] = v; });
      const res = await api.get(`${BASE}/detalle`, { params });
      setDetalle({ data: res.data.data, total: res.data.total, totalPaginas: res.data.totalPaginas });
      setExpandida(null);
    } catch (err) {
      console.error(err);
      setMensaje({ tipo: 'error', texto: 'No se pudo cargar el detalle.' });
    } finally {
      setCargandoDetalle(false);
    }
  }

  useEffect(() => {
    (async () => {
      try {
        setCargando(true);
        const filas = await cargarResumen();
        const cerrado = filas.find((r) => !r.es_parcial) || filas[0];
        const f = cerrado ? { ...filtrosVacios, ...rangoDeMes(cerrado.mes) } : filtrosVacios;
        setFiltros(f);
        await cargarDetalle(f, 1, porPagina);
        setError(null);
      } catch (err) {
        console.error(err);
        setError('No se pudo cargar el KPI de Días de Colocación de OC.');
      } finally {
        setCargando(false);
      }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function handleSincronizar() {
    try {
      setSincronizando(true);
      setMensaje(null);
      const res = await api.post('/api/kpis/sync/dias-colocacion-oc');
      await Promise.all([cargarResumen(), cargarDetalle()]);
      const r = res.data.resultado;
      setMensaje({ tipo: 'ok', texto: `Sincronizado: ${r.insertados} OCs nuevas, ${r.actualizados} actualizadas.` });
    } catch (err) {
      console.error(err);
      setMensaje({ tipo: 'error', texto: err.response?.data?.error || 'No se pudo sincronizar.' });
    } finally {
      setSincronizando(false);
    }
  }

  function irADetalle(mes, extra = {}) {
    const f = { ...filtrosVacios, ...rangoDeMes(mes), ...extra };
    setFiltros(f);
    setPagina(1);
    setTab('detalle');
    cargarDetalle(f, 1);
  }

  async function guardarComentario(fila, comentario) {
    try {
      await api.patch(`${BASE}/${encodeURIComponent(fila.numero_documento)}/comentario`, { comentario: comentario || null });
      fila.comentario = comentario;
      setMensaje({ tipo: 'ok', texto: `Comentario de la OC ${fila.numero_documento} guardado.` });
      return true;
    } catch (err) {
      console.error(err);
      setMensaje({ tipo: 'error', texto: err.response?.status === 403 ? 'No tienes permiso de captura en Compras.' : 'No se pudo guardar el comentario.' });
      return false;
    }
  }

  // ─── Derivados ───
  const conApp = useMemo(() => [...resumen].reverse().filter(mesConApp), [resumen]);
  const mesCerrado = resumen.find((r) => !r.es_parcial) || null;
  const idxCerrado = mesCerrado ? resumen.indexOf(mesCerrado) : -1;
  const previo = idxCerrado >= 0 ? resumen.slice(idxCerrado + 1).find((r) => !r.es_parcial && mesConApp(r)) : null;
  const mesEnCurso = resumen.find((r) => r.es_parcial) || null;
  const etiqueta = (r) => nombreMesCorto(r.mes) + (r.es_parcial ? '*' : MESES_ARRANQUE.includes(claveMes(r.mes)) ? '†' : '');

  const configTiempos = useMemo(() => ({
    type: 'line',
    data: {
      labels: conApp.map(etiqueta),
      datasets: [
        {
          label: 'Desde la requisición',
          data: conApp.map((r) => num(r.dias_promedio)),
          borderColor: COLOR.morado, backgroundColor: COLOR.morado, pointRadius: 5, tension: 0.25,
          pointBackgroundColor: conApp.map((r) => (r.es_parcial ? '#fff' : COLOR[semaforoDias(r.dias_promedio, meta)])),
        },
        {
          label: 'Desde la aprobación final',
          data: conApp.map((r) => num(r.dias_aprob_promedio)),
          borderColor: COLOR.azul, backgroundColor: COLOR.azul, pointRadius: 4, tension: 0.25,
        },
        { label: `Meta (${meta} días)`, data: conApp.map(() => meta), borderColor: COLOR.verde, borderDash: [6, 4], borderWidth: 1.5, pointRadius: 0 },
      ],
    },
    options: {
      maintainAspectRatio: false,
      scales: { y: { beginAtZero: true, title: { display: true, text: 'Días hábiles (promedio)' } } },
      plugins: { legend: { position: 'bottom' } },
    },
  }), [conApp, meta]); // eslint-disable-line react-hooks/exhaustive-deps

  const configComposicion = useMemo(() => ({
    type: 'bar',
    data: {
      labels: conApp.map(etiqueta),
      datasets: [
        { label: 'Con requisición', data: conApp.map((r) => Number(r.con_requisicion)), backgroundColor: COLOR.verde },
        { label: 'Pago de factura', data: conApp.map((r) => Number(r.pago_factura)), backgroundColor: COLOR.azul },
        { label: 'Sin folio', data: conApp.map((r) => Number(r.sin_folio)), backgroundColor: COLOR.amarillo },
        { label: 'Folio no encontrado', data: conApp.map((r) => Number(r.folio_no_encontrado)), backgroundColor: COLOR.rojo },
      ],
    },
    options: {
      maintainAspectRatio: false,
      scales: { x: { stacked: true }, y: { stacked: true, title: { display: true, text: 'OCs' } } },
      plugins: { legend: { position: 'bottom' } },
    },
  }), [conApp]); // eslint-disable-line react-hooks/exhaustive-deps

  const configPorcentajes = useMemo(() => ({
    type: 'line',
    data: {
      labels: conApp.map(etiqueta),
      datasets: [
        { label: '% colocadas antes de la aprobación final', data: conApp.map((r) => num(r.pct_antes_aprobacion)), borderColor: COLOR.rojo, backgroundColor: COLOR.rojo, pointRadius: 4, tension: 0.25 },
        { label: '% OCs sin requisición', data: conApp.map((r) => num(r.pct_sin_requisicion)), borderColor: COLOR.amarillo, backgroundColor: COLOR.amarillo, pointRadius: 4, tension: 0.25, borderDash: [5, 4] },
      ],
    },
    options: {
      maintainAspectRatio: false,
      scales: { y: { beginAtZero: true, max: 100, ticks: { callback: (v) => `${v}%` } } },
      plugins: { legend: { position: 'bottom' }, tooltip: { callbacks: { label: (c) => `${c.dataset.label}: ${fmtPct(c.raw)}` } } },
    },
  }), [conApp]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Excel ───
  function excelHistorico() {
    const ws = XLSX.utils.json_to_sheet([...resumen].reverse().map((r) => ({
      Mes: nombreMes(r.mes) + (r.es_parcial ? ' (parcial)' : MESES_ARRANQUE.includes(claveMes(r.mes)) ? ' (arranque de la app)' : ''),
      'Días desde requisición': num(r.dias_promedio),
      'Días desde aprobación final': num(r.dias_aprob_promedio),
      'Meta (días)': meta,
      'OCs totales': Number(r.total_ordenes),
      'Con requisición': Number(r.con_requisicion),
      'Pago de factura': Number(r.pago_factura),
      'Sin folio': Number(r.sin_folio),
      'Folio no encontrado': Number(r.folio_no_encontrado),
      'Antes de la app': Number(r.antes_app || 0),
      '% sin requisición': num(r.pct_sin_requisicion),
      'Colocadas antes de la aprobación': Number(r.antes_aprobacion),
      '% antes de la aprobación': num(r.pct_antes_aprobacion),
      'Requisición posterior a la OC': Number(r.requi_posterior_oc),
    })));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Histórico');
    XLSX.writeFile(wb, 'Dias_colocacion_OC_historico.xlsx');
  }

  async function excelDetalle() {
    try {
      setExportando(true);
      const params = { exportar: '1' };
      Object.entries(filtros).forEach(([k, v]) => { if (v) params[k] = v; });
      const res = await api.get(`${BASE}/detalle`, { params });
      const ws = XLSX.utils.json_to_sheet(res.data.data.map((o) => ({
        OC: o.numero_documento,
        'Fecha OC': fmtFecha(o.fecha_oc),
        Proveedor: o.proveedor || '',
        'Importe (sin IVA)': num(o.importe),
        Clasificación: CLASIFICACION[o.clasificacion]?.label || o.clasificacion || '',
        Requisición: o.folios_vinculados || o.folio_vinculado || '',
        'Folios no encontrados': o.folios_no_encontrados || '',
        'Folio capturado en la OC': o.folio_app_compras || '',
        'Ligada por': o.vinculo === 'oc_netsuite' ? 'Nota de Compras en la app' : o.vinculo === 'folio' ? 'Folio en la OC' : '',
        Solicitante: o.solicitante || '',
        Urgencia: o.urgencia || '',
        'Emisión requisición': fmtFecha(o.fecha_emision_requi),
        'Días desde requisición': o.dias_desde_requi,
        'Aprobación final': fmtFechaHora(o.fecha_aprobacion_final),
        'Días desde aprobación': o.dias_desde_aprobacion,
        '¿Antes de la aprobación?': o.clasificacion !== 'con_requisicion' ? '' : o.oc_antes_aprobacion ? 'Sí' : o.fecha_aprobacion_final ? 'No' : '',
        'Nota de Compras (app)': o.nota_oc_app || '',
        Comentario: o.comentario || '',
      })));
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Detalle');
      XLSX.writeFile(wb, `Dias_colocacion_OC${filtros.desde ? `_${filtros.desde}_a_${filtros.hasta}` : ''}.xlsx`);
    } catch (err) {
      console.error(err);
      setMensaje({ tipo: 'error', texto: 'No se pudo generar el Excel.' });
    } finally {
      setExportando(false);
    }
  }

  if (cargando) return <div style={s.paginaContenedor}>Cargando...</div>;
  if (error) return <div style={{ ...s.paginaContenedor, color: COLOR.rojo }}>{error}</div>;

  const mesSelValor = resumen.some((r) => { const g = rangoDeMes(r.mes); return g.desde === filtros.desde && g.hasta === filtros.hasta; }) ? filtros.desde : '';

  return (
    <div style={s.paginaContenedor} className="contenedor-captura">
      <div style={{ ...s.headerFila, flexWrap: 'wrap', gap: 12, marginBottom: 14 }}>
        <div>
          <h1 style={{ ...s.tituloPagina, marginBottom: 2 }}>Días de Colocación de OC</h1>
          <div style={{ fontSize: 12, color: COLOR.gris }}>
            Última sincronización: {ultimaSync ? new Date(ultimaSync).toLocaleString('es-MX') : 'sin registro'}
          </div>
        </div>
        <button onClick={handleSincronizar} disabled={sincronizando} style={{ ...s.botonGuardarChico, display: 'flex', alignItems: 'center', gap: 6 }}>
          <FiRefreshCw /> {sincronizando ? 'Sincronizando...' : 'Sincronizar NetSuite y App de Compras'}
        </button>
      </div>

      {mensaje && <div style={{ ...s.mensaje, color: mensaje.tipo === 'ok' ? COLOR.verde : COLOR.rojo }}>{mensaje.texto}</div>}

      <div style={s.tabBar}>
        {[['resumen', 'Resumen'], ['historico', 'Histórico'], ['detalle', 'Detalle de OCs']].map(([k, label]) => (
          <button key={k} onClick={() => setTab(k)} style={{ ...s.tabBtn, ...(tab === k ? s.tabBtnActivo : {}) }}>{label}</button>
        ))}
      </div>

      {/* ═══════════ RESUMEN ═══════════ */}
      {tab === 'resumen' && mesCerrado && (
        <>
          <div style={{ ...s.kLbl, marginBottom: 10 }}>Mes cerrado: {nombreMes(mesCerrado.mes)}</div>
          <div style={s.kgrid}>
            <TarjetaRequi fila={mesCerrado} previo={previo} meta={meta} />
            <TarjetaAprobacion fila={mesCerrado} previo={previo} />
            <TarjetaAntes fila={mesCerrado} onVer={() => irADetalle(mesCerrado.mes, { aprobacion: 'antes' })} />
            <TarjetaSinRequi fila={mesCerrado} onVer={() => irADetalle(mesCerrado.mes, { clasificacion: 'sin_requisicion' })} />
          </div>

          {mesEnCurso && (
            <div style={{ ...s.tarjeta, borderTop: `3px dashed ${COLOR.gris}` }}>
              <div style={s.kLbl}>
                En curso: {nombreMes(mesEnCurso.mes)} <Tag tipo="amarillo">parcial, día {new Date().getDate()}</Tag>
              </div>
              <div style={{ display: 'flex', gap: 28, flexWrap: 'wrap', fontSize: 13, marginTop: 6 }}>
                <span>Desde requisición: <strong>{fmtDias(mesEnCurso.dias_promedio)}</strong> días</span>
                <span>Desde aprobación: <strong>{fmtDias(mesEnCurso.dias_aprob_promedio)}</strong> días</span>
                <span>Antes de la aprobación: <strong>{mesEnCurso.antes_aprobacion}</strong> de {mesEnCurso.con_aprobacion}</span>
                <span>Sin requisición: <strong>{mesEnCurso.sin_requisicion}</strong> · Pago de factura: <strong>{mesEnCurso.pago_factura}</strong></span>
                <button style={s.linkChico} onClick={() => irADetalle(mesEnCurso.mes)}>Ver OCs</button>
              </div>
            </div>
          )}

          <div style={s.seccionKpiBloque}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
              <FiInfo style={{ color: COLOR.morado, marginTop: 2, flexShrink: 0 }} />
              <div style={s.seccionKpiFormula}>
                <strong style={{ color: '#2A1830' }}>Cómo se calcula.</strong> Días hábiles (lunes a viernes, sin contar festivos) entre dos fechas y la fecha de la OC en NetSuite,
                igual que DIAS.LAB − 1 en Excel: mismo día = 0.
                <strong> Desde la requisición</strong> mide el tiempo total que espera el área que pidió (meta: {meta} días o menos).
                <strong> Desde la aprobación final</strong> mide lo que tarda Compras una vez que la requisición tiene todas las firmas;
                solo cuenta las OCs colocadas el mismo día o después de la última firma.
                <br />
                <strong style={{ color: '#2A1830' }}>Aparte:</strong> las OCs colocadas antes de la aprobación final (se compró antes de tener todas las firmas),
                los pagos de factura (la compra ya estaba hecha), y las OCs sin requisición (sin folio, o con un folio que no existe en la App de Compras).
                <br />
                <strong style={{ color: '#2A1830' }}>De dónde sale.</strong> Órdenes de compra de NetSuite (customsearch727) cruzadas con las requisiciones de la App de Compras,
                por el folio capturado en la OC o, si no trae, por el número de OC que Compras anota en la requisición.
              </div>
            </div>
          </div>
        </>
      )}

      {/* ═══════════ HISTÓRICO ═══════════ */}
      {tab === 'historico' && (
        <>
          <div style={s.tarjeta}>
            <div style={s.seccionTitulo}>Días de colocación por mes</div>
            <Grafica config={configTiempos} />
            <div style={{ ...s.kSub, marginTop: 8 }}>* Mes en curso, parcial. † Arranque de la App de Compras: pocas OCs pasaban por la app.</div>
          </div>
          <div style={s.grid2}>
            <div style={s.tarjeta}>
              <div style={s.seccionTitulo}>OCs por tipo</div>
              <Grafica config={configComposicion} alto={260} />
            </div>
            <div style={s.tarjeta}>
              <div style={s.seccionTitulo}>Fuera de proceso</div>
              <Grafica config={configPorcentajes} alto={260} />
            </div>
          </div>

          <div style={{ ...s.headerFila, margin: '6px 0 8px' }}>
            <h3 style={s.subtituloLista}>Tabla mensual</h3>
            <button onClick={excelHistorico} style={{ ...s.botonGuardarChico, display: 'flex', alignItems: 'center', gap: 6 }}><FiDownload /> Descargar Excel</button>
          </div>
          <div style={s.subtitulo}>Haz clic en un mes para ver sus OCs.</div>
          <div style={s.tablaScroll}>
            <table style={s.tabla}>
              <thead>
                <tr>
                  <th style={s.th}>Mes</th>
                  <th style={{ ...s.th, textAlign: 'right' }}>Desde requi</th>
                  <th style={{ ...s.th, textAlign: 'right' }}>Desde aprobación</th>
                  <th style={{ ...s.th, textAlign: 'right' }}>OCs</th>
                  <th style={{ ...s.th, textAlign: 'right' }}>Con requi</th>
                  <th style={{ ...s.th, textAlign: 'right' }}>Pago factura</th>
                  <th style={{ ...s.th, textAlign: 'right' }}>Sin requi</th>
                  <th style={{ ...s.th, textAlign: 'right' }}>Antes de aprobar</th>
                </tr>
              </thead>
              <tbody>
                {resumen.map((r) => {
                  const sinApp = !mesConApp(r);
                  return (
                    <tr key={r.mes} onClick={() => irADetalle(r.mes)} style={{ cursor: 'pointer', ...(sinApp ? { opacity: 0.6 } : {}) }}>
                      <td style={s.td}>
                        {nombreMes(r.mes)}
                        {r.es_parcial && <Tag tipo="amarillo">parcial</Tag>}
                        {MESES_ARRANQUE.includes(claveMes(r.mes)) && <Tag title="Pocas OCs pasaban por la App de Compras">arranque de la app</Tag>}
                        {sinApp && <Tag>antes de la app</Tag>}
                      </td>
                      <td style={{ ...s.td, textAlign: 'right', fontWeight: 700, color: r.es_parcial ? COLOR.gris : COLOR[semaforoDias(r.dias_promedio, meta)] }}>{fmtDias(r.dias_promedio)}</td>
                      <td style={{ ...s.td, textAlign: 'right' }}>{fmtDias(r.dias_aprob_promedio)}</td>
                      <td style={{ ...s.td, textAlign: 'right' }}>{r.total_ordenes}</td>
                      <td style={{ ...s.td, textAlign: 'right' }}>{r.con_requisicion}</td>
                      <td style={{ ...s.td, textAlign: 'right' }}>{r.pago_factura}</td>
                      <td style={{ ...s.td, textAlign: 'right' }}>{sinApp ? '—' : `${r.sin_requisicion} (${fmtPct(r.pct_sin_requisicion)})`}</td>
                      <td style={{ ...s.td, textAlign: 'right' }}>{Number(r.con_aprobacion) ? `${r.antes_aprobacion} de ${r.con_aprobacion} (${fmtPct(r.pct_antes_aprobacion)})` : '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* ═══════════ DETALLE ═══════════ */}
      {tab === 'detalle' && (
        <>
          <div style={s.filtrosBar}>
            <div style={s.filtroGrupo}>
              <span style={s.filtroLabel}>Mes</span>
              <select style={s.filtroInput} value={mesSelValor} onChange={(e) => setFiltros(e.target.value ? { ...filtros, ...rangoDeMes(e.target.value + 'T00:00:00') } : { ...filtros, desde: '', hasta: '' })}>
                <option value="">Rango personalizado</option>
                {resumen.map((r) => <option key={r.mes} value={rangoDeMes(r.mes).desde}>{nombreMes(r.mes)}{r.es_parcial ? ' (parcial)' : ''}</option>)}
              </select>
            </div>
            <div style={s.filtroGrupo}>
              <span style={s.filtroLabel}>Desde</span>
              <input type="date" style={s.filtroInput} value={filtros.desde} onChange={(e) => setFiltros({ ...filtros, desde: e.target.value })} />
            </div>
            <div style={s.filtroGrupo}>
              <span style={s.filtroLabel}>Hasta</span>
              <input type="date" style={s.filtroInput} value={filtros.hasta} onChange={(e) => setFiltros({ ...filtros, hasta: e.target.value })} />
            </div>
            <div style={s.filtroGrupo}>
              <span style={s.filtroLabel}>Tipo</span>
              <select style={s.filtroInput} value={filtros.clasificacion} onChange={(e) => setFiltros({ ...filtros, clasificacion: e.target.value })}>
                <option value="">Todas</option>
                <option value="con_requisicion">Con requisición</option>
                <option value="pago_factura">Pago de factura</option>
                <option value="sin_requisicion">Sin requisición (ambas)</option>
                <option value="sin_folio">Sin folio</option>
                <option value="folio_no_encontrado">Folio no encontrado</option>
                <option value="antes_app">Antes de la app</option>
              </select>
            </div>
            <div style={s.filtroGrupo}>
              <span style={s.filtroLabel}>Aprobación</span>
              <select style={s.filtroInput} value={filtros.aprobacion} onChange={(e) => setFiltros({ ...filtros, aprobacion: e.target.value })}>
                <option value="">Todas</option>
                <option value="antes">Colocadas antes de la aprobación</option>
                <option value="despues">Colocadas después de la aprobación</option>
              </select>
            </div>
            <div style={s.filtroGrupo}>
              <span style={s.filtroLabel}>Buscar</span>
              <input
                type="text"
                placeholder="OC, proveedor, folio o solicitante"
                style={{ ...s.filtroInput, minWidth: 210 }}
                value={filtros.busqueda}
                onChange={(e) => setFiltros({ ...filtros, busqueda: e.target.value })}
                onKeyDown={(e) => { if (e.key === 'Enter') { setPagina(1); cargarDetalle(filtros, 1); } }}
              />
            </div>
            <button style={s.botonBuscar2} onClick={() => { setPagina(1); cargarDetalle(filtros, 1); }}>Buscar</button>
            <button style={s.linkChico} onClick={() => { setFiltros(filtrosVacios); setPagina(1); cargarDetalle(filtrosVacios, 1); }}>Limpiar filtros</button>
          </div>

          <div style={{ ...s.headerFila, marginBottom: 8, flexWrap: 'wrap', gap: 8 }}>
            <div style={{ fontSize: 12, color: COLOR.gris }}>{cargandoDetalle ? 'Cargando...' : `${detalle.total.toLocaleString('es-MX')} OCs`} · Abre una fila para ver la línea de tiempo de la requisición.</div>
            <button onClick={excelDetalle} disabled={exportando || detalle.total === 0} style={{ ...s.botonGuardarChico, display: 'flex', alignItems: 'center', gap: 6 }}>
              <FiDownload /> {exportando ? 'Generando...' : 'Descargar Excel'}
            </button>
          </div>

          <div style={s.tablaScroll}>
            <table style={s.tabla}>
              <thead>
                <tr>
                  <th style={s.th}></th>
                  <th style={s.th}>OC</th>
                  <th style={s.th}>Fecha OC</th>
                  <th style={s.th}>Proveedor</th>
                  <th style={s.th}>Requisición</th>
                  <th style={{ ...s.th, textAlign: 'right' }}>Desde requi</th>
                  <th style={s.th}>Aprobación final</th>
                  <th style={{ ...s.th, textAlign: 'right' }}>Desde aprobación</th>
                  <th style={s.th}>Comentario / justificación</th>
                </tr>
              </thead>
              <tbody>
                {!cargandoDetalle && detalle.data.length === 0 && (
                  <tr><td colSpan={9} style={{ ...s.td, color: COLOR.gris, padding: 16 }}>No hay OCs con estos filtros.</td></tr>
                )}
                {detalle.data.map((o) => {
                  const abierta = expandida === o.numero_documento;
                  const cls = CLASIFICACION[o.clasificacion];
                  const antes = o.oc_antes_aprobacion && o.clasificacion === 'con_requisicion';
                  return (
                    <Fragment key={o.numero_documento}>
                      <tr style={antes ? { background: '#FDF3F1' } : undefined}>
                        <td style={s.td}>
                          <button style={x.botonExpandir} onClick={() => setExpandida(abierta ? null : o.numero_documento)} aria-label={abierta ? 'Cerrar' : 'Ver línea de tiempo'}>
                            {abierta ? <FiChevronUp /> : <FiChevronDown />}
                          </button>
                        </td>
                        <td style={{ ...s.td, fontWeight: 600 }}>{o.numero_documento}</td>
                        <td style={s.td}>{fmtFecha(o.fecha_oc)}</td>
                        <td style={{ ...s.td, whiteSpace: 'normal', minWidth: 170 }}>{o.proveedor || '—'}</td>
                        <td style={{ ...s.td, whiteSpace: 'normal', minWidth: 150 }}>
                          {o.folios_vinculados || o.folio_vinculado || (o.folio_app_compras ? <span style={{ color: COLOR.rojo }}>{o.folio_app_compras}</span> : '—')}
                          {o.folios_vinculados && o.folios_no_encontrados && (
                            <div style={{ color: COLOR.rojo, fontSize: 11.5 }}>No existe en la app: {o.folios_no_encontrados}</div>
                          )}
                          <div>
                            {cls && <Tag tipo={cls.tipo}>{cls.label}</Tag>}
                            {o.vinculo === 'oc_netsuite' && <Tag tipo="gris" title="La OC no traía folio; se ligó por el número de OC que Compras anotó en la requisición">ligada por nota</Tag>}
                            {o.urgencia === 'urgente' && <Tag tipo="rojo">urgente</Tag>}
                          </div>
                        </td>
                        <td style={{ ...s.td, textAlign: 'right', fontWeight: 700, color: o.clasificacion === 'con_requisicion' ? COLOR[semaforoDias(o.dias_desde_requi, meta)] : COLOR.gris }}>
                          {o.dias_desde_requi === null ? '—' : o.dias_desde_requi}
                          {num(o.dias_desde_requi) < 0 && <div><Tag tipo="amarillo" title="La requisición tiene fecha posterior a la OC; no entra al promedio">requi posterior</Tag></div>}
                        </td>
                        <td style={s.td}>{fmtFechaHora(o.fecha_aprobacion_final)}</td>
                        <td style={{ ...s.td, textAlign: 'right', fontWeight: 700 }}>
                          {o.dias_desde_aprobacion === null ? '—' : (
                            antes
                              ? <span style={{ color: COLOR.rojo }}>{o.dias_desde_aprobacion}<div><Tag tipo="rojo"><FiAlertTriangle size={10} /> antes de aprobar</Tag></div></span>
                              : o.dias_desde_aprobacion
                          )}
                        </td>
                        <td style={s.td}>
                          <ComentarioInput fila={o} onGuardar={guardarComentario} />
                        </td>
                      </tr>
                      {abierta && (
                        <tr>
                          <td colSpan={9} style={x.celdaDetalle}>
                            <LineaDeTiempo oc={o} />
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div style={x.paginacion}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={s.filtroLabel}>Por página</span>
              <select style={s.inputChico} value={porPagina} onChange={(e) => { const pp = Number(e.target.value); setPorPagina(pp); setPagina(1); cargarDetalle(filtros, 1, pp); }}>
                {[25, 50, 100, 200].map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <button style={x.botonPagina} disabled={pagina <= 1 || cargandoDetalle} onClick={() => { const p = pagina - 1; setPagina(p); cargarDetalle(filtros, p); }} aria-label="Página anterior"><FiChevronLeft /></button>
              <span style={{ fontSize: 12 }}>Página {pagina} de {detalle.totalPaginas}</span>
              <button style={x.botonPagina} disabled={pagina >= detalle.totalPaginas || cargandoDetalle} onClick={() => { const p = pagina + 1; setPagina(p); cargarDetalle(filtros, p); }} aria-label="Página siguiente"><FiChevronRight /></button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ─── Tarjetas ───
function Variacion({ actual, previo, menorEsMejor = true }) {
  if (num(actual) === null || num(previo) === null) return null;
  const d = num(actual) - num(previo);
  const bueno = menorEsMejor ? d < 0 : d > 0;
  return (
    <div style={{ ...s.kSub, color: d === 0 ? COLOR.gris : bueno ? COLOR.verde : COLOR.rojo }}>
      {d > 0 ? '▲' : d < 0 ? '▼' : '='} {Math.abs(d).toFixed(2)} vs mes anterior ({fmtDias(previo)})
    </div>
  );
}

function TarjetaRequi({ fila, previo, meta }) {
  const v = num(fila.dias_promedio);
  const sem = semaforoDias(v, meta);
  return (
    <div style={{ ...s.kcard, borderTop: `3px solid ${COLOR[sem]}` }}>
      <div style={s.kLbl}>Desde la requisición</div>
      <div style={{ ...s.kVal, fontSize: 32, color: COLOR[sem] }}>{fmtDias(v)} <span style={x.unidad}>días</span></div>
      <div style={{ ...x.veredicto, color: COLOR[sem] }}>
        {v === null ? 'Sin datos' : v <= meta ? `Cumple la meta de ${meta} días` : `No cumple la meta de ${meta} días (+${(v - meta).toFixed(2)})`}
      </div>
      <div style={s.kSub}>{fila.con_requisicion} OCs con requisición.</div>
      <Variacion actual={v} previo={previo?.dias_promedio} />
    </div>
  );
}

function TarjetaAprobacion({ fila, previo }) {
  const v = num(fila.dias_aprob_promedio);
  const despues = Number(fila.con_aprobacion) - Number(fila.antes_aprobacion);
  return (
    <div style={{ ...s.kcard, borderTop: `3px solid ${COLOR.azul}` }}>
      <div style={s.kLbl}>Desde la aprobación final</div>
      <div style={{ ...s.kVal, fontSize: 32, color: COLOR.azul }}>{fmtDias(v)} <span style={x.unidad}>días</span></div>
      <div style={{ ...x.veredicto, color: COLOR.gris }}>Tiempo de Compras con todas las firmas</div>
      <div style={s.kSub}>Promedio de {despues} OCs colocadas el mismo día o después de la última firma.</div>
      <Variacion actual={v} previo={previo?.dias_aprob_promedio} />
    </div>
  );
}

function TarjetaAntes({ fila, onVer }) {
  const pct = num(fila.pct_antes_aprobacion);
  const color = pct === null || pct === 0 ? COLOR.verde : pct < 25 ? COLOR.amarillo : COLOR.rojo;
  return (
    <div style={{ ...s.kcard, borderTop: `3px solid ${color}` }}>
      <div style={s.kLbl}>Colocadas antes de la aprobación final</div>
      <div style={{ ...s.kVal, fontSize: 32, color }}>{fmtPct(pct)}</div>
      <div style={{ ...x.veredicto, color: COLOR.gris }}>{fila.antes_aprobacion} de {fila.con_aprobacion} OCs con requisición aprobada</div>
      <div style={s.kSub}>Se colocó la OC antes de tener todas las firmas. No entran al promedio desde la aprobación.</div>
      <button style={{ ...s.linkChico, marginTop: 8, padding: 0 }} onClick={onVer}>Ver cuáles</button>
    </div>
  );
}

function TarjetaSinRequi({ fila, onVer }) {
  const pct = num(fila.pct_sin_requisicion);
  return (
    <div style={{ ...s.kcard, borderTop: `3px solid ${COLOR.amarillo}` }}>
      <div style={s.kLbl}>OCs sin requisición</div>
      <div style={{ ...s.kVal, fontSize: 32, color: COLOR.amarillo }}>{fila.sin_requisicion}</div>
      <div style={{ ...x.veredicto, color: COLOR.gris }}>{fmtPct(pct)} de las OCs que deberían traer requisición</div>
      <div style={s.kSub}>
        {fila.sin_folio} sin folio · {fila.folio_no_encontrado} con folio que no existe en la app. Aparte: {fila.pago_factura} pagos de factura.
      </div>
      <button style={{ ...s.linkChico, marginTop: 8, padding: 0 }} onClick={onVer}>Ver cuáles</button>
    </div>
  );
}

// ─── Comentario (se guarda al salir del campo) ───
function ComentarioInput({ fila, onGuardar }) {
  const [valor, setValor] = useState(fila.comentario || '');
  const [estado, setEstado] = useState(null); // 'guardando' | 'ok'
  useEffect(() => { setValor(fila.comentario || ''); }, [fila.numero_documento, fila.comentario]);

  async function handleBlur() {
    if (valor === (fila.comentario || '')) return;
    setEstado('guardando');
    const ok = await onGuardar(fila, valor);
    setEstado(ok ? 'ok' : null);
    if (ok) setTimeout(() => setEstado(null), 1500);
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <input
        type="text"
        value={valor}
        placeholder="Agregar justificación..."
        onChange={(e) => setValor(e.target.value)}
        onBlur={handleBlur}
        onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
        style={{ ...s.inputChico, width: 210 }}
      />
      {estado === 'guardando' && <span style={x.unidad}>Guardando...</span>}
      {estado === 'ok' && <span style={{ ...x.unidad, color: COLOR.verde }}>✓</span>}
    </div>
  );
}

// ─── Línea de tiempo de la requisición ───
function LineaDeTiempo({ oc }) {
  if (!oc.folio_vinculado) {
    return (
      <div style={{ fontSize: 12, color: COLOR.gris }}>
        {oc.clasificacion === 'antes_app'
          ? 'OC anterior a la App de Compras: no hay requisición que mostrar.'
          : oc.folio_app_compras
            ? `La OC trae el folio "${oc.folio_app_compras}", pero no existe en la App de Compras. Revisa si está mal capturado.`
            : 'La OC no trae folio de requisición y ninguna requisición menciona este número de OC.'}
      </div>
    );
  }

  const eventos = [];
  if (oc.fecha_emision_requi) eventos.push({ fecha: aFecha(oc.fecha_emision_requi), texto: `Requisición ${oc.folio_vinculado} emitida${oc.solicitante ? ` por ${oc.solicitante}` : ''}`, tipo: 'morado', soloFecha: true });
  (oc.historial_estados || []).forEach((h) => {
    if (!h.fecha) return;
    eventos.push({ fecha: aFecha(h.fecha), texto: `${ESTADO_REQUI[h.de] || h.de || '—'} → ${ESTADO_REQUI[h.a] || h.a}`, tipo: /rechazada/.test(h.a || '') ? 'rojo' : 'gris' });
  });
  (oc.aprobaciones || []).forEach((a) => {
    if (!a.firmado_en) return;
    eventos.push({
      fecha: aFecha(a.firmado_en),
      texto: `Firma nivel ${a.nivel_orden ?? '?'} · ${ROL[a.rol] || a.rol || ''}${a.aprobador ? ` — ${a.aprobador}` : ''}${a.estado === 'rechazada' ? ' (RECHAZADA)' : ''}`,
      tipo: a.estado === 'rechazada' ? 'rojo' : 'azul',
    });
  });
  const fechaOC = aFecha(oc.fecha_oc);
  if (fechaOC) {
    const finDelDia = new Date(fechaOC.getFullYear(), fechaOC.getMonth(), fechaOC.getDate(), 23, 59);
    eventos.push({ fecha: finDelDia, texto: `OC ${oc.numero_documento} colocada en NetSuite`, tipo: oc.oc_antes_aprobacion ? 'rojo' : 'verde', soloFecha: true, esOC: true });
  }
  eventos.sort((a, b) => a.fecha - b.fecha);
  const aprob = aFecha(oc.fecha_aprobacion_final);
  const varias = (oc.folios_vinculados || '').split(',').filter(Boolean).length > 1;

  return (
    <div>
      <div style={{ fontSize: 11, fontWeight: 700, color: '#6E4F80', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 8 }}>
        Línea de tiempo {oc.oc_antes_aprobacion && oc.clasificacion === 'con_requisicion' && <Tag tipo="rojo">la OC se colocó antes de la última firma</Tag>}
      </div>
      {varias && (
        <div style={{ ...x.unidad, marginLeft: 0, marginBottom: 8 }}>
          Esta OC junta varias requisiciones ({oc.folios_vinculados}). Se muestra la más antigua ({oc.folio_vinculado}), que es la que cuenta para los días desde la requisición;
          los días desde la aprobación usan la última aprobación final de todas.
        </div>
      )}
      <div style={x.timeline}>
        {eventos.map((e, i) => (
          <div key={i} style={x.evento}>
            <span style={{ ...x.punto, background: COLOR[e.tipo] || COLOR.gris }} />
            <span style={x.eventoFecha}>{e.soloFecha ? e.fecha.toLocaleDateString('es-MX') : e.fecha.toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'short' })}</span>
            <span style={{ fontSize: 12.5, fontWeight: e.esOC ? 700 : 400 }}>{e.texto}</span>
            {e.esOC && <span style={x.unidad}>(NetSuite no guarda la hora de la OC)</span>}
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', marginTop: 10, fontSize: 12 }}>
        <span>Aprobación final: <strong>{aprob ? aprob.toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'short' }) : '—'}</strong></span>
        {oc.metodo_pago && <span>Método de pago: <strong>{oc.metodo_pago}</strong></span>}
        {oc.nota_oc_app && <span>Nota de Compras en la app: <em>"{oc.nota_oc_app}"</em></span>}
      </div>
    </div>
  );
}

const x = {
  veredicto: { fontSize: 12.5, fontWeight: 600, margin: '6px 0 6px' },
  unidad: { fontSize: 11, color: '#8A7690', fontWeight: 400, marginLeft: 2 },
  tag: { display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 10, fontWeight: 600, padding: '2px 7px', borderRadius: 10, marginLeft: 6, marginTop: 2, whiteSpace: 'nowrap', textTransform: 'none', letterSpacing: 0 },
  tag_gris: { color: '#6E4F80', background: '#F0E9E0' },
  tag_verde: { color: '#2F6146', background: '#E3F0E8' },
  tag_amarillo: { color: '#7a5417', background: '#F9F0DD' },
  tag_rojo: { color: '#B23A48', background: '#F7E5E7' },
  tag_azul: { color: '#2F5D8A', background: '#E3EDF7' },
  paginacion: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10, marginTop: 12 },
  botonPagina: { width: 30, height: 30, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #E6DDD2', background: '#fff', borderRadius: 6, cursor: 'pointer', color: '#4A1E5C' },
  botonExpandir: { width: 26, height: 26, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #E6DDD2', background: '#fff', borderRadius: 6, cursor: 'pointer', color: '#4A1E5C' },
  celdaDetalle: { padding: '14px 18px 16px 44px', background: '#FAF6F0', borderBottom: '1px solid #F0E9E0' },
  timeline: { display: 'flex', flexDirection: 'column', gap: 6, borderLeft: '2px solid #E6DDD2', paddingLeft: 14, marginLeft: 4 },
  evento: { display: 'flex', alignItems: 'center', gap: 10, position: 'relative', flexWrap: 'wrap' },
  punto: { width: 10, height: 10, borderRadius: '50%', position: 'absolute', left: -20, top: 4 },
  eventoFecha: { fontSize: 11.5, color: '#8A7690', minWidth: 120 },
};